import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors} from "@angular/forms";
import {Observable} from "rxjs";
import {map, startWith} from "rxjs/operators";
import {InterfaceProviderService} from "../../../basic-entity-back/services/interface-provider.service";
import {Pais} from "../../../model/localizacion/pais.model";
import {Provincia} from "../../../model/localizacion/provincia.model";
import {DireccionPersona} from "../../../model/personas/direccion-persona.model";

/**
 * Validador de grupo para un formulario de dirección (mismos campos que renderiza este componente).
 * Extraído de cliente-edition.component.ts para poder reutilizarlo desde cualquier formulario de
 * dirección nuevo (p.ej. el de asociado-edition) sin duplicar la lógica. cliente-edition.component.ts
 * conserva su propia copia (validarDireccionFn) sin tocar, para no arriesgar su comportamiento ya en
 * producción.
 */
export function validarDireccionFormGroup(control: AbstractControl): ValidationErrors | null {
    if (control) {
        const controles = (control as UntypedFormGroup).controls;
        for (const [index, con] of Object.entries(controles)) {
            (con as UntypedFormControl).validator = (contr: AbstractControl): ValidationErrors | null => {
                if (index !== 'id' && index !== 'mismaDireccion' && (con.value === "" || con.value === null || (index === 'pais' && !(con.value instanceof Pais)) || (index === 'demarcacion' && !(con.value instanceof Provincia)))) {
                    return {invalid: true};
                }
            };
        }
    }
    return null;
}

/**
 * Campos reactivos de una dirección (tipo, calle, población, CP, provincia, país), extraídos de
 * cliente-edition.component.ts (Tarea 9) para reutilizarlos también en el diálogo de clientes
 * asociados. Recibe el FormGroup ya construido por el padre (con controles 'tipoDireccion', 'calle',
 * 'poblacion', 'cp', 'demarcacion', 'pais') y sólo se ocupa de pintarlo y de alimentar el autocompletar
 * de provincia/país; no decide qué pasa al guardar ni si la dirección es de envío o facturación -eso
 * sigue siendo responsabilidad de quien construye el formulario.
 *
 * :host {display:contents} en el scss: así los mat-form-field quedan como hijos directos del
 * contenedor flex del padre (.newForm en cliente-edition), igual que antes de extraerlos.
 */
@Component({
    selector: 'app-direccion-form',
    templateUrl: './direccion-form.component.html',
    styleUrls: ['./direccion-form.component.scss']
})
export class DireccionFormComponent implements OnInit {
    @Input() formGroup: UntypedFormGroup;

    public paises: Pais[] = [];
    public provincias: Provincia[] = [];
    public filteredPaises: Observable<Pais[]>;
    public filteredProvincias: Observable<Provincia[]>;
    public dirEnvio = DireccionPersona.TIPOS_DIRECCION.ENVIO;
    public dirFact = DireccionPersona.TIPOS_DIRECCION.FACTURACION;

    constructor(private _intf: InterfaceProviderService) {
    }

    ngOnInit(): void {
        this._intf.managerForModel(Provincia).loader.findAndFollow().pipe(map(page => page.member)).subscribe(provincias => {
            this.provincias.push(...provincias);
        });
        this._intf.managerForModel(Pais).loader.findAndFollow([]).pipe(map(page => page.member)).subscribe(paises => {
            this.paises.push(...paises);
        });

        this.filteredPaises = this.formGroup.get('pais').valueChanges.pipe(startWith(''), map(value => {
            if (value) {
                const pais = this.formGroup.get('pais').value;
                const provincia = this.formGroup.get('demarcacion').value;
                if (pais instanceof Pais && provincia instanceof Provincia && pais.iri.toString() !== provincia.pais.toString()) {
                    this.formGroup.get('demarcacion').reset();
                }

                return this.paises.filter(p => {
                    return p.nombreDefault.toLowerCase().includes(value.nombreDefault?.toLowerCase() ?? value.toLowerCase())
                });
            }
        }));

        this.filteredProvincias = this.formGroup.get('demarcacion').valueChanges.pipe(startWith(''), map(value => {
            if (value) {
                const pais = this.formGroup.get('pais').value;
                const provincia = this.formGroup.get('demarcacion').value;
                if (pais instanceof Pais && provincia instanceof Provincia && pais.iri.toString() !== provincia.pais.toString()) {
                    this.formGroup.get('pais').reset();
                }

                return this.provincias.filter(provincia => {
                    return provincia.nombre.toLowerCase().includes(value.nombre?.toLowerCase() ?? value.toLowerCase())
                });
            }
        }));
    }

    control(name: string): UntypedFormControl {
        return this.formGroup.get(name) as UntypedFormControl;
    }

    displayProperty(value) {
        if (value && value.nombreDefault) {
            return value.nombreDefault;
        } else if (value && value.nombre) {
            return value.nombre;
        }
    }
}
